import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Buildings, CheckCircle, ArrowRight } from '@phosphor-icons/react';

const ForDevelopersSection = () => {
  const navigate = useNavigate();

  const features = [
    { title: 'Access retail investors', desc: 'Tap into a network of investors looking for quality real estate projects.' },
    { title: 'Digital fundraising', desc: 'Modern platform for seamless capital raising and investor onboarding.' },
    { title: 'Automated reporting', desc: 'Streamlined investor updates and performance reporting.' },
    { title: 'Compliance support', desc: 'We handle regulatory requirements and documentation.' },
    { title: 'Marketing exposure', desc: 'Showcase your projects to qualified investors.' },
    { title: 'Investor management', desc: 'Full-service investor relations and communications.' },
  ];

  return (
    <section className="relative py-20 md:py-28 overflow-hidden" data-testid="for-developers-section">
      {/* Background image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url("https://customer-assets-gfyr7b9c.emergentagent.net/job_fd06b94e-7c79-4247-931d-e490fe4e5eb1/artifacts/e9c3xdtn_7697799c-3d10-4ecc-9d09-2ab0ac79f7d6.png")',
          backgroundPosition: 'center bottom'
        }}
      />
      {/* Dark overlay - reduced for more picture visibility */}
      <div className="absolute inset-0 bg-slate-900/70" />
      
      <div className="container mx-auto px-6 md:px-12 lg:px-20 relative z-10">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white text-sm font-medium mb-4">
            <Buildings size={18} weight="duotone" />
            For Developers
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-medium mb-4 tracking-tight text-white">
            <span className="text-primary">Raise capital.</span><br />
            We handle the rest.
          </h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            Access a network of retail investors.<br />
            We handle the fundraising and investor management.
          </p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {features.map((item, index) => (
            <div key={index} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <CheckCircle className="text-primary mb-3" size={24} weight="fill" />
              <h3 className="text-lg font-heading font-semibold mb-2 text-white">{item.title}</h3>
              <p className="text-white/70 text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </motion.div>

        <motion.div 
          className="text-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Button
            size="lg"
            className="rounded-full px-10 py-7 text-lg font-medium bg-primary hover:bg-primary-hover text-white transition-all duration-300"
            onClick={() => navigate('/submit-project')}
          >
            List Your Project <ArrowRight className="ml-2" size={20} weight="bold" />
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default ForDevelopersSection;
